import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { PartyPopper } from "lucide-react";

function pad(n) { return String(Math.max(0, n)).padStart(2, "0"); }

const COLORS = ["#ff3b3b", "#f6b64a", "#2fd0c6", "#ffffff"];

function fireConfetti() {
  const end = Date.now() + 2000;
  (function frame() {
    confetti({ particleCount: 6, angle: 60, spread: 75, startVelocity: 55, origin: { x: 0, y: 0.7 }, colors: COLORS });
    confetti({ particleCount: 6, angle: 120, spread: 75, startVelocity: 55, origin: { x: 1, y: 0.7 }, colors: COLORS });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 160, spread: 110, startVelocity: 45, origin: { y: 0.55 }, colors: COLORS, scalar: 1.1 });
}

export default function Countdown({ target, onOpen }) {
  const targetMs = new Date(target).getTime();
  const [now, setNow] = useState(Date.now());
  const [phase, setPhase] = useState("idle"); // idle | drop | open
  const [drop, setDrop] = useState(null);
  const started = useRef(false);

  const startCelebration = () => {
    if (started.current) return;
    started.current = true;
    setPhase("drop");
    const rem = Math.max(0, targetMs - Date.now());
    const base = rem > 60000
      ? { d: Math.floor(rem / 86400000), h: Math.floor((rem % 86400000) / 3600000), m: Math.floor((rem % 3600000) / 60000), s: Math.floor((rem % 60000) / 1000) }
      : { d: 3, h: 14, m: 22, s: 47 }; // flourish snapshot when we're already at/near zero
    const dur = 1700;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(p, 2.3); // races toward 0, easing out
      setDrop({ d: Math.round(base.d * e), h: Math.round(base.h * e), m: Math.round(base.m * e), s: Math.round(base.s * e) });
      if (p < 1) requestAnimationFrame(tick);
      else { setDrop({ d: 0, h: 0, m: 0, s: 0 }); setPhase("open"); fireConfetti(); onOpen && onOpen(); }
    };
    requestAnimationFrame(tick);
  };

  // Decide phase; re-runs if the target changes (e.g. after content loads).
  useEffect(() => {
    const openNow = Date.now() >= targetMs;
    if (openNow) {
      const recentlyOpened = Date.now() - targetMs < 24 * 3600 * 1000;
      if (recentlyOpened) startCelebration();
      else { setPhase("open"); onOpen && onOpen(); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);

  // Live ticker while counting down; triggers celebration at zero.
  useEffect(() => {
    if (phase !== "idle") return;
    const iv = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= targetMs) startCelebration();
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, targetMs]);

  if (phase === "open") {
    return (
      <motion.div
        initial={{ scale: 0.5, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 12 }}
        className="inline-flex items-center gap-2 glass-dark rounded-full px-6 py-3 border border-[#2fd0c6]/40 text-glow-cyan font-display text-xl sm:text-2xl font-extrabold"
        data-testid="countdown-open"
      >
        <PartyPopper size={22} className="text-[#f6b64a]" /> We're Open — Book Your Party!
      </motion.div>
    );
  }

  const live = (() => {
    const diff = Math.max(0, targetMs - now);
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  })();
  const vals = phase === "drop" ? (drop || { d: 0, h: 0, m: 0, s: 0 }) : live;
  const dropping = phase === "drop";
  const units = [
    { label: "Days", value: vals.d },
    { label: "Hours", value: vals.h },
    { label: "Minutes", value: vals.m },
    { label: "Seconds", value: vals.s },
  ];

  return (
    <div className="flex gap-3 sm:gap-4" data-testid="countdown-timer">
      {units.map((u) => (
        <motion.div
          key={u.label}
          animate={dropping ? { scale: [1, 1.12, 1], boxShadow: "0 0 26px rgba(47,208,198,0.45)" } : { scale: 1 }}
          transition={dropping ? { duration: 0.28, repeat: Infinity } : { duration: 0.2 }}
          className="glass-dark rounded-2xl px-3 py-3 sm:px-5 sm:py-4 text-center min-w-[62px] sm:min-w-[84px]"
        >
          <div className={`font-mono-arcade text-2xl sm:text-4xl font-extrabold text-white ${dropping ? "blur-[0.6px]" : ""}`}>{pad(u.value)}</div>
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/50 mt-1">{u.label}</div>
        </motion.div>
      ))}
    </div>
  );
}
